import CardboardBoxIcon from '@assets/icons/cardboard-box.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { EMPTY_VALUE_LABEL } from '@sales-portal/constants';
import { formatCurrency } from '@/utils/money';
import { ItemDetailCard } from '@/experiences/sales-portal/domain/items/components/item-detail-card';
import { PurchaseHistoryEntry } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { toFixedDecimalPercentage } from '@/utils/percentage';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

export interface PurchaseHistoryCardProps {
  entry: PurchaseHistoryEntry;
  hideGpField: boolean;
}

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [styles] = useStyles();
  const theme = useTheme();
  return (
    <View style={styles.stat}>
      <Typography
        color={theme.sk.color.text.secondary}
        numberOfLines={1}
        variant="paragraph-sm"
      >
        {label}
      </Typography>
      <Typography
        color={theme.sk.color.text.primary}
        numberOfLines={1}
        variant="paragraph-md-600"
      >
        {value}
      </Typography>
    </View>
  );
};

/**
 * Single fulfillment card for the Recent purchase history list. Renders a
 * package icon + Fulfilled date stack and the units pill across the top, then
 * Total cost / Per Unit / (optional) GP% in a row below a divider.
 *
 * @param props - {@link PurchaseHistoryCardProps}.
 */
export const PurchaseHistoryCard: React.FC<PurchaseHistoryCardProps> = ({
  entry,
  hideGpField,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  const dateLabel = entry.fulfillmentDateDisplay || EMPTY_VALUE_LABEL;

  const totalCost =
    entry.totalPriceInMicros !== null
      ? formatCurrency(entry.totalPriceInMicros, entry.currencyCode)
      : EMPTY_VALUE_LABEL;

  const perUnit =
    entry.baseUnitPriceInMicros !== null
      ? formatCurrency(entry.baseUnitPriceInMicros, entry.currencyCode)
      : EMPTY_VALUE_LABEL;

  const gpDisplay =
    entry.salesRepGrossProfitInPercentage !== null
      ? toFixedDecimalPercentage(entry.salesRepGrossProfitInPercentage)
      : EMPTY_VALUE_LABEL;

  const unitsLabel =
    entry.quantity !== null
      ? i18n.t(
          entry.quantity === 1
            ? 'sales_portal.order_entry.item_detail.purchase_history.unit'
            : 'sales_portal.order_entry.item_detail.purchase_history.units',
          { count: entry.quantity },
        )
      : null;

  return (
    <ItemDetailCard>
      <View style={styles.topRow}>
        <View style={styles.iconBackground}>
          <CardboardBoxIcon
            height={theme.sk.spacing.spaceMedium}
            stroke={theme.sk.color.icon.primary}
            width={theme.sk.spacing.spaceMedium}
          />
        </View>
        <View style={styles.fulfilledColumn}>
          <Typography
            color={theme.sk.color.text.secondary}
            numberOfLines={1}
            variant="paragraph-md"
          >
            {i18n.t(
              'sales_portal.order_entry.item_detail.purchase_history.fulfilled',
            )}
          </Typography>
          <Typography
            color={theme.sk.color.text.primary}
            numberOfLines={1}
            variant="paragraph-md-600"
          >
            {dateLabel}
          </Typography>
        </View>
        {unitsLabel ? (
          <View style={styles.unitsPill}>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-sm-600"
            >
              {unitsLabel}
            </Typography>
          </View>
        ) : null}
      </View>
      <View style={styles.divider} />
      <View style={styles.statsRow}>
        <Stat
          label={i18n.t(
            'sales_portal.order_entry.item_detail.purchase_history.total_cost',
          )}
          value={totalCost}
        />
        <Stat
          label={i18n.t(
            'sales_portal.order_entry.item_detail.purchase_history.per_unit',
          )}
          value={perUnit}
        />
        {hideGpField ? null : (
          <Stat
            label={i18n.t(
              'sales_portal.order_entry.item_detail.purchase_history.gp_percent',
            )}
            value={gpDisplay}
          />
        )}
      </View>
    </ItemDetailCard>
  );
};

const useStyles = createStyles((theme) => ({
  divider: {
    backgroundColor: theme.sk.color.border.primary,
    height: 1,
    marginVertical: theme.sk.spacing.spaceSmall,
    width: '100%',
  },
  fulfilledColumn: {
    flex: 1,
    flexDirection: 'column',
    marginLeft: theme.sk.spacing.spaceSMedium,
  },
  iconBackground: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.spacing.spaceXlarge / 2,
    height: theme.sk.spacing.spaceXlarge,
    justifyContent: 'center',
    width: theme.sk.spacing.spaceXlarge,
  },
  stat: {
    flex: 1,
    flexDirection: 'column',
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  topRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  unitsPill: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusRound,
    flexDirection: 'row',
    height: theme.sk.spacing.spaceLarge,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
}));
